import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity, BarChart3, Briefcase, Building2, CalendarDays, ChevronRight, CircleCheck,
  CircleDollarSign, FileText, Filter, Inbox, ListFilter, PhoneCall, ReceiptText,
  RefreshCw, TrendingUp, UserRound, Users, Wallet, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiCard } from "@/components/common/KpiCard";
import { NotificationsWidget } from "@/components/common/NotificationsWidget";
import { BackupHealthWidget } from "@/components/common/BackupHealthWidget";
import { ChartCard, FunnelBarChart, SimpleBarChart, CHART_COLORS } from "@/components/common/ChartCard";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { api } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/constants";

const EMPTY_FILTERS = { date_from: "", date_to: "", company_id: "all", recruiter_id: "all" };

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ops, setOps] = useState(null); // existing operational cards (unfiltered)
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [dailyTeam, setDailyTeam] = useState(null);

  useEffect(() => {
    api.get("/dashboard/admin").then((res) => setOps(res.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/companies", { params: { limit: 100 } })
      .then((res) => setCompanies(res.data.items || res.data || []))
      .catch(() => {});
    api.get("/users/recruiters").then((res) => setRecruiters(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    api.get("/dashboard/daily-activity", { params }).then((r) => setDailyTeam(r.data)).catch(() => {});
  }, [filters.recruiter_id]);

  const loadAnalytics = useCallback(() => {
    setAnalyticsLoading(true);
    setAnalyticsError(false);
    const params = {};
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.company_id !== "all") params.company_id = filters.company_id;
    if (filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    api.get("/analytics/admin", { params })
      .then((res) => setAnalytics(res.data))
      .catch(() => setAnalyticsError(true))
      .finally(() => setAnalyticsLoading(false));
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(loadAnalytics, 250);
    return () => clearTimeout(t);
  }, [loadAnalytics]);

  const filtersActive = filters.date_from || filters.date_to || filters.company_id !== "all" || filters.recruiter_id !== "all";

  if (loading || !ops) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <div className="grid sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const a = analytics;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">Central business overview — recruitment, employment and billing at a glance.</p>
        </div>
        {/* M15: backup health light (links to System & Backups) */}
        <BackupHealthWidget />
      </div>

      {/* Operational cards (existing, unfiltered) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button onClick={() => navigate(`/admin/applications?status=${encodeURIComponent("Application Received")}`)} className="text-left" data-testid="admin-card-new-applications">
          <KpiCard label="New Applications" value={ops.cards.new_applications} icon={Inbox} tone="primary" />
        </button>
        <button onClick={() => navigate(`/admin/applications?status=${encodeURIComponent("Application Received")}`)} className="text-left" data-testid="admin-card-not-contacted">
          <KpiCard label="Candidates Not Contacted" value={ops.cards.not_contacted} icon={PhoneCall} tone="warning" />
        </button>
        <button onClick={() => navigate(`/admin/applications?status=${encodeURIComponent("Contact Attempted")}`)} className="text-left" data-testid="admin-card-status-not-updated">
          <KpiCard label="Status Not Updated" value={ops.cards.status_not_updated} icon={RefreshCw} tone="violet" />
        </button>
      </div>

      {/* M13: Notification widget (approved additive integration) */}
      <NotificationsWidget />

      {/* Today's activity — per recruiter + team totals (reuses the recruiter filter below) */}
      {dailyTeam?.rows && (
        <div className="space-y-3" data-testid="daily-activity-section">
          <h2 className="text-base font-heading font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" /> Today's activity
          </h2>
          <Card className="rounded-xl border-border shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="daily-activity-table">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="p-3 font-medium">Recruiter</th>
                    <th className="p-3 font-medium text-right">Claimed today</th>
                    <th className="p-3 font-medium text-right">Scheduled today</th>
                    <th className="p-3 font-medium text-right">Pending call-backs</th>
                    <th className="p-3 font-medium text-right">Forwarded today</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyTeam.rows.length === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No recruiters found.</td></tr>
                  )}
                  {dailyTeam.rows.map((r) => (
                    <tr key={r.recruiter_id} className="border-b border-border/60" data-testid={`daily-row-${r.recruiter_id}`}>
                      <td className="p-3">{r.recruiter_name}{r.active === false && <span className="text-[11px] text-muted-foreground ml-1">(inactive)</span>}</td>
                      <td className="p-3 text-right tabular-nums">{r.claimed_today}</td>
                      <td className="p-3 text-right tabular-nums">{r.scheduled_today}</td>
                      <td className="p-3 text-right tabular-nums">{r.pending_callbacks}</td>
                      <td className="p-3 text-right tabular-nums">{r.forwarded_today}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold bg-muted/40" data-testid="daily-totals-row">
                    <td className="p-3">Team total</td>
                    <td className="p-3 text-right tabular-nums">{dailyTeam.totals.claimed_today}</td>
                    <td className="p-3 text-right tabular-nums">{dailyTeam.totals.scheduled_today}</td>
                    <td className="p-3 text-right tabular-nums">{dailyTeam.totals.pending_callbacks}</td>
                    <td className="p-3 text-right tabular-nums">{dailyTeam.totals.forwarded_today}</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Global filters */}
      <Card className="rounded-xl border-border shadow-none" data-testid="dashboard-filters">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground shrink-0 lg:pb-2.5">
              <Filter className="h-4 w-4" /> Filters
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3 w-3" /> From</label>
                <Input type="date" className="bg-white h-9" value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="dashboard-filter-from" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3 w-3" /> To</label>
                <Input type="date" className="bg-white h-9" value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="dashboard-filter-to" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" /> Company</label>
                <Select value={filters.company_id} onValueChange={(v) => setFilters({ ...filters, company_id: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="dashboard-filter-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All companies</SelectItem>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground flex items-center gap-1"><UserRound className="h-3 w-3" /> Recruiter</label>
                <Select value={filters.recruiter_id} onValueChange={(v) => setFilters({ ...filters, recruiter_id: v })}>
                  <SelectTrigger className="bg-white h-9" data-testid="dashboard-filter-recruiter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All recruiters</SelectItem>
                    {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {filtersActive && (
              <Button variant="ghost" size="sm" className="rounded-lg shrink-0 lg:mb-0.5" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="dashboard-filter-reset">
                <X className="h-3.5 w-3.5 mr-1.5" /> Reset
              </Button>
            )}
          </div>
          {filters.company_id !== "all" && (
            <p className="text-[11px] text-muted-foreground mt-2">Company filter applies to employment and billing data.</p>
          )}
        </CardContent>
      </Card>

      {analyticsError ? (
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="py-10 flex flex-col items-center gap-3" data-testid="dashboard-analytics-error">
            <p className="text-sm text-muted-foreground">Could not load analytics.</p>
            <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={loadAnalytics}>Retry</Button>
          </CardContent>
        </Card>
      ) : analyticsLoading || !a ? (
        <div className="space-y-4" data-testid="dashboard-analytics-loading">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
          <div className="grid lg:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
        </div>
      ) : (
        <>
          {/* Recruitment KPIs */}
          <div className="space-y-3" data-testid="dashboard-recruitment-section">
            <h2 className="text-base font-heading font-semibold flex items-center gap-2"><Briefcase className="h-4 w-4 text-muted-foreground" /> Recruitment</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiCard label="Total Applications" value={a.recruitment.total_applications} icon={Inbox} tone="primary" testId="kpi-total-applications" />
              <KpiCard label="In Pipeline" value={a.recruitment.in_pipeline} icon={ListFilter} tone="slate" testId="kpi-in-pipeline" />
              <KpiCard label="Interviews Scheduled" value={a.recruitment.interviews_scheduled} icon={CalendarDays} tone="violet" testId="kpi-interviews-scheduled" />
              <KpiCard label="Selected" value={a.recruitment.selected} icon={CircleCheck} tone="success" testId="kpi-selected" />
              <KpiCard label="Rejected" value={a.recruitment.rejected} icon={X} tone="warning" testId="kpi-rejected" />
            </div>
          </div>

          {/* Employment KPIs */}
          <div className="space-y-3" data-testid="dashboard-employment-section">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-heading font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /> Employment</h2>
              <Link to="/admin/employment" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="dashboard-employment-link">
                Open Employment Register <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiCard label="Total Joinings" value={a.employment.total} icon={Users} tone="primary" testId="kpi-total-joinings" />
              <KpiCard label="Working" value={a.employment.working} icon={TrendingUp} tone="success" testId="kpi-working" />
              <KpiCard label="Lock Complete" value={a.employment.lock_complete} icon={CircleCheck} tone="violet" testId="kpi-lock-complete" />
              <KpiCard label="Ready for Invoice" value={a.employment.ready_for_invoice} icon={ReceiptText} tone="warning" testId="kpi-emp-ready-for-invoice" />
              <KpiCard label="Left" value={a.employment.left} icon={X} tone="slate" testId="kpi-left" />
            </div>
          </div>

          {/* Billing & Payments KPIs (existing section, now filter-aware) */}
          <div className="space-y-3" data-testid="dashboard-billing-section">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-heading font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-muted-foreground" /> Billing &amp; Payments</h2>
              <Link to="/admin/billing" className="inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="dashboard-billing-link">
                Open Billing Queue <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <button onClick={() => navigate("/admin/billing")} className="text-left" data-testid="dashboard-kpi-ready-for-invoice">
                <KpiCard label="Ready for Invoice" value={a.billing.total_records} icon={ReceiptText} tone="primary" />
              </button>
              <button onClick={() => navigate("/admin/billing")} className="text-left" data-testid="dashboard-kpi-invoice-pending">
                <KpiCard label="Invoice Pending" value={a.billing.invoice_pending} icon={FileText} tone="warning" />
              </button>
              <button onClick={() => navigate("/admin/billing")} className="text-left" data-testid="dashboard-kpi-invoice-raised">
                <KpiCard label="Invoice Raised" value={a.billing.invoice_raised} icon={CircleCheck} tone="violet" />
              </button>
              <button onClick={() => navigate("/admin/billing")} className="text-left" data-testid="dashboard-kpi-payment-pending">
                <KpiCard label="Payment Pending" value={a.billing.payment_pending} icon={Wallet} tone="slate" />
              </button>
              <button onClick={() => navigate("/admin/billing")} className="text-left" data-testid="dashboard-kpi-payment-received">
                <KpiCard label="Payment Received" value={a.billing.payment_received} icon={CircleDollarSign} tone="success" />
              </button>
            </div>
          </div>

          {/* Charts */}
          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard title="Monthly Joinings" icon={BarChart3} testId="chart-monthly-joinings"
              empty={!a.monthly_joinings.some((m) => m.joinings > 0)}
              emptyText="No joinings recorded in the last 12 months for the selected filters.">
              <SimpleBarChart data={a.monthly_joinings} xKey="month"
                bars={[{ key: "joinings", label: "Joinings", color: CHART_COLORS[0] }]} />
            </ChartCard>

            <ChartCard title="Recruitment Pipeline Funnel" icon={ListFilter} testId="chart-pipeline-funnel"
              empty={!a.pipeline_funnel.some((s) => s.count > 0)}
              emptyText="No applications for the selected filters.">
              <FunnelBarChart data={a.pipeline_funnel} />
            </ChartCard>

            <ChartCard title="Recruiter Performance" icon={UserRound} testId="chart-recruiter-performance"
              empty={a.recruiter_performance.length === 0 || !a.recruiter_performance.some((r) => r.applications || r.joined)}
              emptyText="No recruiter activity for the selected filters.">
              <SimpleBarChart data={a.recruiter_performance} xKey="name" showLegend
                bars={[
                  { key: "applications", label: "Applications", color: CHART_COLORS[0] },
                  { key: "selected", label: "Selected", color: CHART_COLORS[3] },
                  { key: "joined", label: "Joined", color: CHART_COLORS[1] },
                ]} />
            </ChartCard>

            <ChartCard title="Company Performance" icon={Building2} testId="chart-company-performance"
              empty={a.company_performance.length === 0}
              emptyText="No employment or billing data linked to companies yet.">
              <SimpleBarChart data={a.company_performance} xKey="name" showLegend
                bars={[
                  { key: "joined", label: "Joinings", color: CHART_COLORS[0] },
                  { key: "ready_for_invoice", label: "Ready for Invoice", color: CHART_COLORS[4] },
                  { key: "payments_received", label: "Payments Received", color: CHART_COLORS[1] },
                ]} />
            </ChartCard>
          </div>

          {/* Recent activity + recent applications */}
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Activity className="h-4 w-4 text-muted-foreground" /> Recent activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto" data-testid="dashboard-recent-activity">
                {a.recent_activity.length === 0 && <p className="text-sm text-muted-foreground">No activity for the selected filters.</p>}
                {a.recent_activity.map((l) => (
                  <div key={l.id} className="rounded-lg border border-border p-2.5" data-testid="activity-item">
                    <div className="flex items-center justify-between gap-2">
                      <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{l.action}</span>
                      <span className="text-[11px] text-muted-foreground font-mono shrink-0">{formatDateTime(l.created_at)}</span>
                    </div>
                    <p className="text-xs mt-1.5 text-foreground/90 line-clamp-2">{l.details}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">by <span className="font-medium text-foreground">{l.actor_name}</span></p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="rounded-xl border-border shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading">Recent applications</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 max-h-96 overflow-y-auto" data-testid="admin-recent-applications">
                {ops.recent_applications.length === 0 && <p className="text-sm text-muted-foreground">No applications yet.</p>}
                {ops.recent_applications.map((r) => (
                  <Link key={r.id} to={`/admin/applications/${r.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 hover:border-primary/40 transition-colors">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.member_name} <span className="font-mono text-xs text-muted-foreground ml-1">{r.code}</span></p>
                      <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5 mt-0.5">
                        <Building2 className="h-3 w-3" />{r.hiring_brand} · {r.job_title} · {formatDate(r.created_at)}
                      </p>
                    </div>
                    <StatusBadge status={r.status} className="text-[10px] shrink-0" />
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
